import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, TextField, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import { Translate } from 'react-localize-redux';
import Modal from '../../../components/general/modal';
import { QcRequestState } from '../QualityCheck/qcRequestState';

const T = 'hospital.my-corrections.confirm';

interface MarkCorrectedDialogProps {
    open: boolean;
    /** How the panel names the visit. */
    visitLabel: string;
    request: QcRequestState;
    /** The note as typed (possibly empty: it is optional). */
    onConfirm: (note: string) => void;
    onCancel: () => void;
}

/**
 * SKIN-600 — the confirmation behind "Mark corrected": nothing is sent until Confirm. Marking a
 * visit corrected hands it back to the QC reviewer. The note is optional and stored in plain
 * text, like every flag note, so the dialog warns against identifying data.
 */
export default function MarkCorrectedDialog({ open, visitLabel, request, onConfirm, onCancel }: MarkCorrectedDialogProps) {
    const [note, setNote] = useState('');
    const submitting = request.status === 'submitting';
    // A new confirmation starts blank; a refused one keeps what was typed.
    useEffect(() => { if (open) setNote(''); }, [open, visitLabel]);

    return (
        <Modal
            open={open}
            title={<Translate id={`${T}.title`} />}
            closeModal={submitting ? () => undefined : onCancel}
            maxWidth="sm"
            fullWidth
        >
            <Box data-testid="mark-corrected-dialog" sx={{ width: '100%' }}>
                <Typography variant="body2" color="text.secondary">
                    <Translate id="hospital.my-corrections.confirm.body" data={{ visit: visitLabel }} />
                </Typography>
                <TextField
                    data-testid="mark-corrected-note"
                    label={<Translate id={`${T}.note-label`} />}
                    value={note}
                    onChange={(event) => setNote(event.target.value)}
                    disabled={submitting}
                    multiline
                    minRows={2}
                    fullWidth
                    sx={{ mt: 2 }}
                />
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
                    <Translate id={`${T}.note-warning`} />
                </Typography>
                {request.status === 'failed' && (
                    <Alert severity="error" data-testid="mark-corrected-failed" sx={{ mt: 2 }}>
                        <Translate id={`${T}.failed`} />
                        {request.error && <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>{request.error}</Typography>}
                    </Alert>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.25, mt: 2.5 }}>
                    <Button variant="outlined" color="inherit" disabled={submitting} onClick={onCancel} data-testid="mark-corrected-cancel">
                        <Translate id="general.cancel" />
                    </Button>
                    <Button variant="contained" disabled={submitting} onClick={() => onConfirm(note)}
                        startIcon={<CheckIcon />} data-testid="mark-corrected-confirm">
                        <Translate id={submitting ? `${T}.saving` : `${T}.confirm`} />
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
}
